import { useState, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/authStore'
import { useRefStore } from '@/store/refStore'
import { useUiStore } from '@/store/uiStore'
import { getClaimByUserIdAndCaseId } from '@/services/claimService'
import { preloadRefCategories } from '@/utils/preloadRefCategories'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ClaimDetailView } from '@/components/claim/ClaimDetailView'
import { exportClaimToPdf } from '@/utils/pdf/exportClaimPdf'
import { getMessage } from '@/utils/messages'
import type { ClaimGetResponse } from '@/types/claim'

export function ClaimViewPage() {
  const location = useLocation()
  const params = useParams<{ caseId: string }>()
  const { user } = useAuthStore()
  const { getLabelById } = useRefStore()
  const { showToast } = useUiStore()

  const stateUserId = (location.state as { userId?: string } | null)?.userId
  const stateCaseId = (location.state as { caseId?: string } | null)?.caseId
  const userId = stateUserId ?? String(user?.userId ?? '')
  const caseId = stateCaseId ?? params.caseId ?? ''

  const [claim, setClaim] = useState<ClaimGetResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    if (!claim) return
    setExporting(true)
    try {
      await exportClaimToPdf(claim, getLabelById)
    } catch (err) {
      showToast(err instanceof Error ? err.message : getMessage('dashboard.export_error'), 'error')
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (!userId || !caseId) {
      setError(getMessage('claim_view_page.error_missing_reference'))
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await getClaimByUserIdAndCaseId(userId, caseId)
        if (res.success && res.data) {
          await preloadRefCategories(res.data)
          if (!cancelled) setClaim(res.data)
        } else if (!cancelled) {
          setError(res.message ?? getMessage('claim_view_page.error_not_found'))
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : getMessage('claim_view_page.error_load_failed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId, caseId])

  if (loading) {
    return (
      <main className="flex-1 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" label={getMessage('claim_view_page.loading')} />
      </main>
    )
  }

  if (error || !claim) {
    return (
      <main className="px-4 lg:px-10 xl:px-20 py-6 max-w-[1400px] mx-auto w-full">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-8 text-center">
          <p className="text-[var(--error)] font-mono text-[13px]">
            {error ?? getMessage('claim_view_page.error_not_found')}
          </p>
        </div>
      </main>
    )
  }

  return (
    <main className="px-4 lg:px-10 xl:px-20 py-6 max-w-[1400px] mx-auto w-full">
      {exporting && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-white/30 backdrop-blur-[1.5px]">
          <LoadingSpinner size="lg" label={getMessage('dashboard.export_generating')} />
        </div>
      )}
      <ClaimDetailView
        claim={claim}
        getLabelById={getLabelById}
        headerRight={
          claim.statusId === 2 && (
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              title={getMessage('dashboard.action_export')}
              aria-label={getMessage('dashboard.action_export')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-[6px] rounded-[var(--radius-sm)]',
                'border border-[var(--border)] bg-[var(--surface2)]',
                'text-[var(--text-dim)] text-[12px] font-semibold',
                'hover:border-[var(--accent)] hover:text-[var(--accent)]',
                'hover:bg-[rgba(61,142,240,0.05)] transition-all duration-[180ms]',
                exporting && 'opacity-60 cursor-not-allowed'
              )}
            >
              <Download size={13} />
              {getMessage('dashboard.action_export')}
            </button>
          )
        }
      />
    </main>
  )
}
